import { Check, Copy, Phone, Shield, User } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { MakeCallModal } from '@/components/common/calls/make-call-modal';
import { cn } from '@/lib/utils';
import { useGetCoveragesQuery } from '@/services/api/coverageService';
import {
  CoverageCategory,
  CoverageSchema,
} from '@/services/types/coverage-types';

import { MatterSectionCard } from './MatterSectionCard';

interface InsuranceCardsProps {
  matterId: string;
}

interface CopyableValueProps {
  label: string;
  value: string | null | undefined;
  className?: string;
}

const CopyableValue = ({ label, value, className }: CopyableValueProps) => {
  const [copied, setCopied] = useState(false);

  if (!value) return null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success(`${label} copied`);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy');
    }
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      className={cn(
        'inline-flex items-center gap-1 text-xs font-medium text-zinc-900',
        'border-b border-dashed border-zinc-300 hover:border-zinc-500',
        'cursor-pointer transition-colors',
        className,
      )}
      title={`Click to copy ${label}`}
    >
      <span>{value}</span>
      {copied ? (
        <Check className="h-3 w-3 text-emerald-500" />
      ) : (
        <Copy className="h-3 w-3 text-zinc-400" />
      )}
    </button>
  );
};

interface ClickablePhoneProps {
  phone: string | null | undefined;
  label?: string;
  matterId: string;
}

const ClickablePhone = ({
  phone,
  label = 'Phone',
  matterId,
}: ClickablePhoneProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  if (!phone) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setIsModalOpen(true)}
        className={cn(
          'inline-flex items-center gap-1 text-xs font-medium',
          'text-primary hover:text-primary/80',
          'cursor-pointer transition-colors',
        )}
        title={`Click to call ${label}`}
      >
        <Phone className="h-3 w-3" />
        <span>{phone}</span>
      </button>
      <MakeCallModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        initialPhoneNumber={phone}
        initialContactLabel={label}
        matterId={matterId}
        source="insurance_card"
      />
    </>
  );
};

interface InsuranceInfoRowProps {
  label: string;
  value: string | null | undefined;
  copyable?: boolean;
  isPhone?: boolean;
  matterId?: string;
  phoneLabel?: string;
}

const InsuranceInfoRow = ({
  label,
  value,
  copyable = false,
  isPhone = false,
  matterId = '',
  phoneLabel,
}: InsuranceInfoRowProps) => {
  if (!value) return null;

  const renderValue = () => {
    if (isPhone) {
      return (
        <ClickablePhone
          phone={value}
          label={phoneLabel || label}
          matterId={matterId}
        />
      );
    }
    if (copyable) {
      return <CopyableValue label={label} value={value} />;
    }
    return (
      <span className="text-xs font-medium text-zinc-900 text-right max-w-[60%] truncate">
        {value}
      </span>
    );
  };

  return (
    <div className="flex justify-between items-center py-0.5">
      <span className="text-[11px] text-zinc-500">{label}</span>
      {renderValue()}
    </div>
  );
};

interface SingleInsuranceCardProps {
  title: string;
  coverage: CoverageSchema | undefined;
  isLoading: boolean;
  type: 'client' | 'defendant';
  matterId: string;
}

const formatCoverageLimits = (coverage: CoverageSchema): string | null => {
  if (!coverage.benefits || coverage.benefits.length === 0) return null;

  const limits: string[] = [];
  coverage.benefits.forEach((benefit) => {
    if (benefit.limitations) {
      Object.entries(benefit.limitations).forEach(([key, limitValue]) => {
        const formattedValue =
          limitValue >= 1000
            ? `$${(limitValue / 1000).toLocaleString()}k`
            : `$${limitValue.toLocaleString()}`;
        limits.push(`${formattedValue} ${key}`);
      });
    }
  });

  return limits.length > 0 ? limits.join(' / ') : null;
};

const getPhoneFromHandles = (
  handles: { handle_type: string; value: string }[] | null,
): string | null => {
  if (!handles) return null;
  const phone = handles.find(
    (h) => h.handle_type === 'PHONE' || h.handle_type === 'phone',
  );
  return phone?.value || null;
};

const getPolicyNumber = (coverage: CoverageSchema): string | null => {
  if (!coverage.policy_identifiers || coverage.policy_identifiers.length === 0)
    return null;
  const policyId = coverage.policy_identifiers.find(
    (pi) => pi.identifier_type === 'POLICY_NUMBER',
  );
  return (
    policyId?.identifier_value ||
    coverage.policy_identifiers[0]?.identifier_value ||
    null
  );
};

const getClaimNumber = (coverage: CoverageSchema): string | null => {
  if (!coverage.claims || coverage.claims.length === 0) return null;
  return coverage.claims[0]?.claim_identifier || null;
};

const getAdjusterInfo = (
  coverage: CoverageSchema,
): { name: string | null; phone: string | null } => {
  if (!coverage.adjusters || coverage.adjusters.length === 0) {
    return { name: null, phone: null };
  }
  const adjuster = coverage.adjusters[0];
  return {
    name: adjuster.name,
    phone: getPhoneFromHandles(adjuster.handles),
  };
};

const SingleInsuranceCard = ({
  title,
  coverage,
  isLoading,
  type,
  matterId,
}: SingleInsuranceCardProps) => {
  if (isLoading) {
    return (
      <MatterSectionCard
        title={title}
        icon={<Shield className="h-4 w-4" />}
        isLoading
      >
        <div />
      </MatterSectionCard>
    );
  }

  if (!coverage) {
    return (
      <MatterSectionCard
        title={title}
        icon={<Shield className="h-4 w-4" />}
        bodyClassName="p-3"
      >
        <p className="text-xs text-zinc-500">No {type} insurance on file</p>
      </MatterSectionCard>
    );
  }

  const carrierName = coverage.carrier?.name || 'Unknown Carrier';
  const policyNumber = getPolicyNumber(coverage);
  const coverageLimits = formatCoverageLimits(coverage);
  const claimNumber = getClaimNumber(coverage);
  const adjusterInfo = getAdjusterInfo(coverage);
  const carrierPhone = getPhoneFromHandles(coverage.carrier?.handles || null);
  const contactPhone = adjusterInfo.phone || carrierPhone;
  const contactLabel = adjusterInfo.name || carrierName;

  return (
    <MatterSectionCard
      title={title}
      icon={<Shield className="h-4 w-4" />}
      bodyClassName="p-3"
    >
      {/* Carrier Name Header */}
      <div className="mb-2 pb-1.5 border-b border-zinc-100">
        <p className="text-xs font-semibold text-zinc-900">{carrierName}</p>
      </div>

      {/* Key Insurance Details */}
      <div className="space-y-0.5">
        {/* Policy Limits - Most important for paralegals */}
        {coverageLimits && (
          <div className="flex justify-between items-center py-0.5">
            <span className="text-[11px] text-zinc-500">Policy Limits</span>
            <span className="text-xs font-semibold text-zinc-900">
              {coverageLimits}
            </span>
          </div>
        )}
        <InsuranceInfoRow label="Policy #" value={policyNumber} copyable />
        {claimNumber && (
          <InsuranceInfoRow label="Claim #" value={claimNumber} copyable />
        )}
      </div>

      {/* Adjuster Contact - Key person to call */}
      {(adjusterInfo.name || contactPhone) && (
        <div className="mt-2 pt-2 border-t border-zinc-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5 min-w-0">
              <User className="h-3.5 w-3.5 text-zinc-400 shrink-0" />
              <div className="min-w-0">
                <span className="text-[10px] text-zinc-400 block">
                  Adjuster
                </span>
                <span className="text-xs font-medium text-zinc-800 truncate block">
                  {adjusterInfo.name || 'Unknown'}
                </span>
              </div>
            </div>
            {contactPhone && (
              <ClickablePhone
                phone={contactPhone}
                label={contactLabel}
                matterId={matterId}
              />
            )}
          </div>
        </div>
      )}

      {/* Carrier Phone fallback if no adjuster */}
      {!adjusterInfo.name && !adjusterInfo.phone && carrierPhone && (
        <div className="mt-2 pt-2 border-t border-zinc-100">
          <div className="flex items-center justify-between">
            <span className="text-[11px] text-zinc-500">Carrier Phone</span>
            <ClickablePhone
              phone={carrierPhone}
              label={carrierName}
              matterId={matterId}
            />
          </div>
        </div>
      )}
    </MatterSectionCard>
  );
};

export const InsuranceCards = ({ matterId }: InsuranceCardsProps) => {
  const { data: coverages, isLoading } = useGetCoveragesQuery({ matterId });

  // Find client (first party) and defendant (third party) coverages
  const clientCoverage = coverages?.find(
    (c) =>
      c.category === CoverageCategory.FIRST_PARTY ||
      c.category === CoverageCategory.FIRST_PARTY_HEALTH,
  );
  const defendantCoverage = coverages?.find(
    (c) => c.category === CoverageCategory.THIRD_PARTY,
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
      <SingleInsuranceCard
        title="Client Insurance"
        coverage={clientCoverage}
        isLoading={isLoading}
        type="client"
        matterId={matterId}
      />
      <SingleInsuranceCard
        title="Defendant Insurance"
        coverage={defendantCoverage}
        isLoading={isLoading}
        type="defendant"
        matterId={matterId}
      />
    </div>
  );
};
